import rimg from '@pixelunion/rimg-shopify';
import * as breakpoint from '@pixelunion/breakpoint';
import EventHandler from '@pixelunion/events';
import Flickity from 'flickity';
import 'flickity-imagesloaded';
import 'flickity-as-nav-for';
import Drift from 'drift-zoom';
import Video from './Video';
import GalleryModal from './GalleryModal';

export default class ProductGallery {
  constructor(options) {
    this.el = options.el;

    this.galleryElements = {
      gallery: this.el.querySelector('[data-main-gallery]'),
      thumbnailGallery: this.el.querySelector('[data-thumbnail-gallery]'),
      slides: this.el.querySelectorAll('[data-product-gallery-slide]'),
      thumbnails: this.el.querySelectorAll('[data-gallery-thumbnail]'),
      arrowShape: 'M95.04 46 21.68 46 48.18 22.8 42.91 16.78 4.96 50 42.91 83.22 48.18 77.2 21.68 54 95.04 54 95.04 46z',
    };

    this.gallerySettings = options.settings;

    if (!this.galleryElements.gallery) return;

    this.events = new EventHandler();

    const {
      gallery,
      thumbnailGallery,
      slides,
      thumbnails,
      arrowShape,
    } = this.galleryElements;

    const {
      mediaCount,
      slideshowSpeed,
      slideshowAnimation,
      thumbnailSliderEnabled,
      thumbnailsArrowsEnabled,
      thumbnailsPosition,
      zoomEnabled,
      lightboxEnabled,
      loopingEnabled,
    } = this.gallerySettings;

    const thumbnailsEnabled = this.gallerySettings.thumbnailsEnabled !== 'disabled';

    rimg.watch(gallery);

    if (thumbnailGallery) {
      rimg.watch(thumbnailGallery);
    }

    // Initialize Flickity for main gallery
    this.flickity = new Flickity(gallery, {
      wrapAround: true,
      adaptiveHeight: true,
      dragThreshold: 10,
      imagesLoaded: true,
      pageDots: mediaCount > 1,
      prevNextButtons: mediaCount > 1,
      autoPlay: slideshowSpeed * 1000,
      fade: slideshowAnimation === 'fade',
      watchCSS: false,
      arrowShape,
      accessibility: false, // Flickity's inherent "accessibility" actually makes it worse
    });

    // Arrows added after Flickity initialized
    this.arrows = this.el.querySelectorAll('.flickity-prev-next-button');

    const videos = gallery.querySelectorAll('[data-html5-video] video, [data-external-video]');

    this.galleryVideos = new Video(videos, {
      loop: {
        active: loopingEnabled,
      },
    });

    Object.keys(this.galleryVideos).forEach(player => {
      this.videoPlayers = this.galleryVideos[player];
    });

    if (!Array.isArray(this.videoPlayers)) {
      this.players = [];
      this.players.push(this.videoPlayers);
    } else {
      this.players = this.videoPlayers;
    }

    this.playVideo = (type, host, id) => {
      this.players.forEach(player => {
        if (player.playing) {
          player.pause();
        }

        if (type === 'video' && player.id === id) {
          player.play();
        }

        if (type === 'external_video') {
          if (host === 'youtube' && player.media.id === id) {
            player.play();
          }

          if (host === 'vimeo' && player.id === id) {
            player.play();
          }
        }

        // On fullscreen exit, focus on the thumbnail carousel
        player.on('exitfullscreen', () => {
          if (thumbnailGallery) {
            thumbnailGallery.focus();
          }
        });
      });
    };

    this.playModel = model => {
      model.play();
    };

    this.disableFlickityDrag = () => {
      this.flickity.options.draggable = false;
      this.flickity.updateDraggable();
    };

    this.enableFlickityDrag = () => {
      this.flickity.options.draggable = true;
      this.flickity.updateDraggable();
    };

    this.initModal = () => {
      if (!this.lightboxImages) { return; }
      this.lightboxImages.forEach((image, selectedIndex) => {
        const { href, dataset } = image;
        const item = {
          src: href,
          w: dataset.imageWidth,
          h: dataset.imageHeight,
        };

        this.lightboxImageArray.push(item);
        this.events.register(image, 'click', e => {
          e.preventDefault();
          this.modal.init(this.lightboxImageArray, selectedIndex);
        });
      });
    };

    this._updateTabindex = () => {
      // We need to remove tabindex from the slides that are not currently
      // selected, so that slides not currently shown cannot receive keyboard focus.
      slides.forEach((slide, i) => {
        const links = slide.querySelectorAll('a');
        const isVisible = this.flickity.selectedIndex === i;

        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        links.forEach(link => link.setAttribute('tabindex', isVisible ? '0' : '-1'));
      });
    };

    // Lightbox
    if (lightboxEnabled) {
      this.lightboxImages = this.el.querySelectorAll('[data-product-gallery-modal]');
      this.lightboxImageArray = [];
      this.modal = new GalleryModal();

      if (this.lightboxImages.length > 0) {
        this.initModal();
      }
    }

    this._updateTabindex();

    // Handling models and videos during Flickity change event
    this.flickity.on('change', () => {
      slides.forEach(slide => {
        const mediaType = slide.getAttribute('data-media-type');
        if (slide.classList.contains('is-selected')) {
          switch (mediaType) {
            case 'model':
              if (breakpoint.min('L')) {
                // On mouseenter event, unbind flickity
                this.events.register(slide, 'mouseenter', this.disableFlickityDrag);

                // On mouseleave event, bind flickity
                this.events.register(slide, 'mouseleave', this.enableFlickityDrag);
              }

              // Listen for model play/pause events
              this.events.register(slide.querySelector('model-viewer'), 'shopify_model_viewer_ui_toggle_play', this.disableFlickityDrag);

              this.events.register(slide.querySelector('model-viewer'), 'shopify_model_viewer_ui_toggle_pause', this.enableFlickityDrag);
              break;
            default:
          }
        } else {
          // Below logic deals with the hidden slides when a flickity change is triggered
          switch (mediaType) {
            case 'video': {
              const videoID = parseInt(slide.querySelector('video').getAttribute('data-plyr-video-id'), 10);
              if (this.players && videoID) {
                this.players.forEach(player => {
                  if (player.id === videoID) {
                    player.pause();
                  }
                });
              }
              break;
            }
            case 'external_video': {
              const host = slide.getAttribute('data-media-host');
              if (this.players) {
                this.players.forEach(player => {
                  if (host === 'youtube') {
                    const iframeID = slide.querySelector('iframe').getAttribute('id');
                    if (player.media.id === iframeID) {
                      player.pause();
                    }
                  }

                  if (host === 'vimeo') {
                    const videoWrapper = slide.querySelector('[data-plyr-video-id]');
                    if (!videoWrapper) { return; }
                    const videoID = parseInt(videoWrapper.getAttribute('data-plyr-video-id'), 10);
                    if (player.id === videoID) {
                      player.pause();
                    }
                  }
                });
              }
              break;
            }
            default:
          }
        }
      });

      this._updateTabindex();
    });

    this.flickity.on('settle', () => {
      // Ensures Flickity is not collapsed when loaded
      this.resizeFlickity();

      // If zoom enabled, initiate Drift on gallery images
      if (zoomEnabled && breakpoint.min('L')) {
        const selectedSlide = this.el.querySelector('.product-gallery__slide.is-selected');

        if (!selectedSlide) { return; }

        const selectedImage = selectedSlide.querySelector('[data-rimg]');

        if (!selectedImage) { return; }

        selectedImage.setAttribute('data-zoom', selectedSlide.getAttribute('data-zoom'));

        this.drift = new Drift(selectedImage, {
          paneContainer: selectedSlide,
        });
      }

      // If lightbox enabled, disable pointer events when dragging
      if (lightboxEnabled) {
        this.flickity.on('dragStart', () => {
          this.el.querySelector('.product-gallery__slide.is-selected').style.pointerEvents = 'none';
        });

        this.flickity.on('dragEnd', () => {
          this.el.querySelector('.product-gallery__slide.is-selected').style.pointerEvents = 'all';
        });
      }
    });

    this.resizeFlickity = () => {
      this.flickity.resize();
    };

    this.resizeThumbnailFlickity = () => {
      if (this.thumbnailFlickity) {
        this.thumbnailFlickity.resize();
      }
    };

    this.handleSlideMedia = () => {
      this.flickity.stopPlayer();
      this.flickity.on('settle', () => {
        const slide = gallery.querySelector('.product-gallery__slide.is-selected');
        const mediaType = slide.getAttribute('data-media-type');
        const host = slide.getAttribute('data-media-host');
        if (mediaType === 'video') {
          const videoID = parseInt(slide.querySelector('video').getAttribute('data-plyr-video-id'), 10);
          this.playVideo('video', host, videoID);
        }

        if (mediaType === 'external_video') {
          if (host === 'youtube') {
            const iframeID = slide.querySelector('iframe').getAttribute('id');
            this.playVideo('external_video', host, iframeID);
          }

          if (host === 'vimeo') {
            const videoID = parseInt(slide.querySelector('[data-plyr-video-id]').getAttribute('data-plyr-video-id'), 10);
            this.playVideo('external_video', host, videoID);
          }
        }

        if (mediaType === 'model') {
          if (this.models) {
            this.models.forEach(model => {
              const modelSlide = model.container.closest('.product-gallery__slide');
              if (modelSlide.classList.contains('is-selected')) {
                this.playModel(model);
              }
            });
          }
        }
      });

      this.flickity.off('settle');
    };

    this.events.register(window, 'load', this.resizeFlickity);

    // Logic for selected slide on page load
    slides.forEach(slide => {
      const mediaType = slide.getAttribute('data-media-type');
      if (slide.classList.contains('is-selected')) {
        if (mediaType === 'model' && breakpoint.min('L')) {
          this.events.register(slide, 'mouseenter', this.disableFlickityDrag);

          this.events.register(slide, 'mouseleave', this.enableFlickityDrag);
        }
      }
    });

    if ((thumbnailsArrowsEnabled || thumbnailsEnabled) && breakpoint.min('L')) {
      if (this.arrows) {
        this.arrows.forEach(arrow => {
          // Clicking slider arrows
          this.events.register(arrow, 'click', this.handleSlideMedia);
        });
      }

      if (thumbnails) {
        thumbnails.forEach((thumbnail, index) => {
          // Clicking thumbnails
          this.events.register(thumbnail, 'click', () => {
            this.flickity.select(index);
          });
          this.events.register(thumbnail, 'click', this.handleSlideMedia);
        });
      }
    }

    // Thumbnail gallery
    if (thumbnailsEnabled) {
      if (thumbnailSliderEnabled && slides.length > 1) {
        if (breakpoint.min('S')) {
          if (thumbnailsPosition === 'bottom-thumbnails') {
            this.thumbnailFlickity = new Flickity(thumbnailGallery, {
              wrapAround: true,
              cellAlign: 'left',
              contain: true,
              groupCells: '80%',
              imagesLoaded: true,
              pageDots: false,
              prevNextButtons: thumbnails.length > 5 ? thumbnailsArrowsEnabled : false,
              asNavFor: gallery,
              arrowShape,
            });

            // Ensures Flickity is not collapsed when loaded
            this.thumbnailFlickity.on('settle', () => {
              this.thumbnailFlickity.resize();
            });

            this.events.register(window, 'load', this.resizeThumbnailFlickity);

            // Once thumbnail is focused, move carousel to that cell
            thumbnails.forEach((thumbnail, index) => {
              this.events.register(thumbnail, 'focus', () => {
                this.thumbnailFlickity.select(index);
              });
            });
          }
        } else {
          // Create standard thumbnail slider
          this.thumbnailFlickity = new Flickity(thumbnailGallery, {
            wrapAround: true,
            cellAlign: 'left',
            contain: true,
            groupCells: true,
            imagesLoaded: true,
            pageDots: false,
            prevNextButtons: false,
            asNavFor: gallery,
            arrowShape,
          });
        }

        this.events.register(window, 'resize', this.resizeThumbnailFlickity);
      } else {
        // If thumbnail slider is disabled, ensure thumbnails still link to product images
        thumbnails.forEach((thumbnail, index) => {
          this.events.register(thumbnail, 'click', () => {
            this.flickity.select(index);
          });
        });
      }
    }

    // 3D Model setup with default control list
    this.modelsConfig = {
      controls: [
        'zoom-in',
        'zoom-out',
        'fullscreen',
      ],
      focusOnPlay: false,
    };

    // Loading product media libraries
    window.Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
      },
      {
        name: 'model-viewer-ui',
        version: '1.0',
      },
    ], () => {
      this.models = [];
      this.modelElements = this.el.querySelectorAll('model-viewer');
      if (this.modelElements) {
        this.modelElements.forEach(modelViewer => {
          const model = new Shopify.ModelViewerUI(modelViewer, this.modelsConfig);
          this.models.push(model);
        });
      }
    });
  }

  updateGalleryImageByVariant(variant) {
    if (this.flickity && variant.featured_media) {
      const index = this.galleryElements.gallery.querySelector(`[data-image-id="${variant.featured_media.id}"]`).getAttribute('data-index');
      this.flickity.select(index, false, true);
    }
  }

  unload() {
    if (this.flickity) {
      this.flickity.destroy();
    }

    if (this.thumbnailFlickity) {
      this.thumbnailFlickity.destroy();
    }

    if (this.drift) {
      this.drift.disable();
      this.drift = null;
    }

    if (this.galleryVideos) {
      this.galleryVideos.unload();
    }

    if (this.events) {
      this.events.unregisterAll();
    }
  }
}
